<template>
  <div ref="gamDropdown" :class="['gam-dropdown', getClasses]">
    <div class="gam-dropdown-container" :style="getDropdownStyle">
      <GamSimpleIntersect v-bind="getDropdownIntersect" ref="intersect" @infinite="infiniteLoad?.()">
        <div v-if="numOfItems === 0 && !isLoading" class="no-data-info">
          <div class="message">
            <div class="message-icon">
              <gam-icon :name="GamIconName.SEARCH" />
            </div>
            <div class="message-text" v-html="getNoDataText"></div>
          </div>
        </div>

        <div class="gam-dropdown-content" :style="getDropdownContentStyle">
          <gam-button
            v-if="defaultItem"
            v-bind="getButtonItem(defaultItem as GamDropdownItem, -1)"
            :class="getDefaultClass"
          />

          <template v-for="(item, index) in dropdownItems" :key="`results-${category}-${index}`">
            <div
              v-if="isLocationDropdown() && index > 0 && item.source !== dropdownItems![index - 1].source"
              class="dropdown-divider"
            />
            <slot name="dropdown-item" :item="item" :set-selected-item="setSelectedItem" :index="index">
              <gam-button v-bind="getButtonItem(item, index)" ref="itemRef" :key="`${index}-${item.id}`" />
            </slot>
          </template>
        </div>
      </GamSimpleIntersect>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BaseLocationDto } from '@/core/data/location/location.interface';
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { localizeWithValues } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamDropdownCategory, GamDropdownSize } from '@/views/composables/constants/components/gamDropdown.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamPlayerCardExpose } from '@/views/composables/models/components/GamPlayer';
import type { GamSimpleIntersectType } from '@/views/composables/models/components/GamSimpleIntersect';
import { whenever } from '@vueuse/core';
import { computed, onMounted, ref, type StyleValue } from 'vue';
import GamSimpleIntersect from './GamSimpleIntersect.vue';

interface DropdownProps<ITEM_TYPE> {
  query: string;
  items: any[];
  category?: GamDropdownCategory;
  size?: GamDropdownSize;
  defaultItem?: ITEM_TYPE | null;
  selected?: ITEM_TYPE | null;
  maxItems?: number;
  itemSpacing?: number;
  multiselect?: boolean;
  isOpen?: boolean;
  isReversed?: boolean;
  isLoading?: boolean;
  infiniteLoad: () => void;
  afterCursor?: string | null;
}

const props = withDefaults(defineProps<DropdownProps<BaseUserDto | BaseLocationDto | GamDropdownItem>>(), {
  maxItems: 4,
  itemSpacing: 8,
  infiniteLoad: () => {},
  category: GamDropdownCategory.DEFAULT,
  size: GamDropdownSize.SMALL,
  selected: null,
  defaultItem: null,
  afterCursor: null,
});
const emits = defineEmits([GamComponentsEmits.UPDATE]);

const dropdownItems = ref<any[] | null>(null);

const gamDropdown = ref<HTMLDivElement>();
const intersect = ref();
const itemRef = ref<GamPlayerCardExpose[]>();
const numOfItems = ref<number | null>(null);

const selectedItem = ref<GamDropdownItem>();

onMounted(() => {
  dropdownItems.value = props.items;
});

const getStaticHeight = (): number => {
  const staticHeight = props.size === GamDropdownSize.SMALL ? 34 : 52;
  return isPlayerDropdown() || isClubOrganizerDropdown() ? 47 : staticHeight;
};

const getNumOfItems = (): number => {
  if (props.query.length) {
    if (dropdownItems.value?.length && props.defaultItem) {
      return dropdownItems.value.length + 1;
    }
    return dropdownItems.value?.length || 0;
  } else if (props.defaultItem) {
    return (dropdownItems.value?.length || 0) + 1;
  } else if (!isLocationDropdown()) {
    return dropdownItems.value?.length || 0;
  }
  return 0;
};

const getDropdownMaxHeight = (): number => {
  if (getNumOfItems() === 0) return 0;
  const itemHeight: number = itemRef.value?.[0]?.getHeight || getStaticHeight();
  const spacingHeight = props.itemSpacing * (props.maxItems - 1);

  return itemHeight * props.maxItems + spacingHeight + 18;
};
const getDropdownHeight = (): number => {
  if (getNumOfItems() === 0) return 0;
  const itemHeight: number = itemRef.value?.[0]?.getHeight || getStaticHeight();
  const maxItems = getNumOfItems() < props.maxItems ? getNumOfItems() : props.maxItems;
  const spacingHeight = props.itemSpacing * (maxItems - 1);

  const height = itemHeight * maxItems + spacingHeight + 15;

  return height;
};

const getButtonItem = (item: GamDropdownItem, index: number): GamButtonType => {
  return {
    label: item.label,
    subLabel: item.subLabel,
    leftIcon: item.icon,
    size: props.size === GamDropdownSize.SMALL ? GamButtonSize.ACTIONS : GamButtonSize.LARGE,
    variant: GamButtonVariant.TERTIARY,
    isIconWhite: true,
    isFullSize: item.isFullSize,
    onClick: () => {
      if (!(props.defaultItem && index < 0)) {
        setSelectedItem(index);
      }
    },
  };
};

const isClubOrganizerDropdown = (): boolean => {
  return props.category === GamDropdownCategory.CLUB_ORGANIZERS;
};

const isPlayerDropdown = (): boolean => {
  return props.category === GamDropdownCategory.PLAYERS;
};

const isLocationDropdown = (): boolean => {
  return props.category === GamDropdownCategory.LOCATIONS;
};

const setSelectedItem = (index: number) => {
  selectedItem.value = props.items[index];
  emits(GamComponentsEmits.UPDATE, selectedItem.value);
};

const setNumItems = (): number | null => {
  if (!isLocationDropdown()) {
    return dropdownItems.value?.length || 0;
  } else {
    return getNumOfItems();
  }
};

const getDropdownIntersect = computed((): GamSimpleIntersectType => {
  return {
    items: props.items,
    showMore: !!props.afterCursor,
    noData: false,
    dropdown: true,
    widthSpacing: 8,
  };
});

const getDropdownStyle = computed((): StyleValue => {
  const height = getDropdownHeight();
  const maxHeight = getDropdownMaxHeight();

  return {
    height: height ? `${height}px` : 'auto',
    maxHeight: maxHeight ? `${maxHeight}px` : 'none',
  };
});

const getDropdownContentStyle = computed((): StyleValue => {
  return {
    gap: !isLocationDropdown() ? `${props.itemSpacing}px` : '0',
    padding: isMobile.value ? '0 0 0 0' : '0 0 0 8px',
  };
});

const getClasses = computed(() => {
  return {
    'no-data': numOfItems.value === 0,
    'is-open': dropdownItems.value,
    'is-mobile': isMobile.value,
    default: props.category === GamDropdownCategory.DEFAULT,
    reversed: props.isReversed,
  };
});

const getDefaultClass = computed(() => {
  return {
    default: getNumOfItems() > 1,
  };
});

const getNoDataText = computed((): string => {
  if (!props.query) return '';

  return localizeWithValues('app.component.search.missing', [props.query]);
});

whenever(
  () => props.items,
  () => {
    dropdownItems.value = props.items;
    numOfItems.value = setNumItems();
  },
);
</script>

<style scoped lang="scss">
.gam-dropdown {
  width: 100%;
  height: fit-content;
  position: absolute;
  top: 56px;
  z-index: 800;
  overflow: hidden;
  pointer-events: none;
  -webkit-backdrop-filter: blur(15px);
  backdrop-filter: blur(15px);
  border-radius: var(--radius-small);
  border: 1px solid var(--color-white-10);

  &.is-open {
    pointer-events: auto;
  }

  &.reversed {
    bottom: 56px;
    top: auto;
  }

  &.no-data {
    background-color: var(--gambit-white-white-10, rgba(255, 255, 255, 0.1));
  }

  &.default {
    .gam-dropdown-container {
      min-height: auto;
    }
  }

  .gam-dropdown-container {
    width: 100%;
    height: auto;
    background-color: var(--color-dark-30);
    padding: var(--spacing-md) 0;

    .gam-dropdown-content {
      height: 100%;
      flex-direction: column;
      align-items: center;
      display: flex;
      min-width: 0;
      overflow: hidden;

      :deep(.gam-button-container.right) {
        padding-right: var(--spacing-md);
      }

      .default {
        position: relative;

        &:after {
          content: '';
          width: 100%;
          height: 1px;
          background-color: var(--color-white-10);
          position: absolute;
          bottom: 0;
        }
      }
    }

    .dropdown-divider {
      align-self: stretch;
      height: 1px;
      margin: var(--spacing-md) var(--spacing-l);
      background-color: var(--color-white-10);
      flex-shrink: 0;
    }

    .no-data-info {
      padding: var(--spacing-md);
      display: flex;
      align-items: center;
      flex-direction: column;

      .message {
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: var(--spacing-l);
        padding: var(--spacing-l);
        align-self: stretch;

        .message-text {
          font-size: 16px;
          font-weight: 300;
          flex: 1 0 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: pre-wrap;
        }
      }
    }
  }
}
</style>
